import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faFilter as fasFilter } from '@fortawesome/pro-solid-svg-icons';

import { Button } from 'src/common/components/buttons/Button';
import { SelectSitelife } from 'src/common/components/SelectSitelife';
import { SelectSitelifeOptionType } from 'src/common/components/SelectSitelife/types';
import { Paper } from 'src/common/components/Paper';
import { cn } from 'src/common/utils';
import { FilterOption } from 'src/common/types';
import { LicenseType } from 'src/project/types/projects';

// Define the filter state type with company ID and license type
export type ProjectFilter2State = {
  companyId: string[]; // company/organization filter
  licenseType: string[]; // license type filter
  status: string[]; // status filter
  workspace: string; // workspace filter - Yes/No
  fileTypes: string[]; // file types filter - 2D, 3D, empty (client-side only)
};

// Default filter state with empty arrays
export const defaultFilter2State: ProjectFilter2State = {
  companyId: [],
  licenseType: [],
  status: [],
  workspace: '',
  fileTypes: [],
};

// Props for the Filter2 component
type Props = {
  filter2State: ProjectFilter2State;
  onApply: (filter2State: ProjectFilter2State) => void;
  companies: FilterOption[];
  className?: string;
};

// Filter2 component for filtering projects by company and license type
export const ProjectsFilter2 = ({
  filter2State,
  onApply,
  companies,
  className,
}: Props) => {
  const { t } = useTranslation(['admin', 'common']);
  const [isOpen, setIsOpen] = useState(false);
  const [localFilter, setLocalFilter] =
    useState<ProjectFilter2State>(filter2State);
  // Add separate states for tracking selected values within the component
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>(
    filter2State.companyId,
  );
  const [selectedLicenseTypes, setSelectedLicenseTypes] = useState<string[]>(
    filter2State.licenseType,
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    filter2State.status,
  );
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>(
    filter2State.workspace,
  );
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>(
    filter2State.fileTypes,
  );

  // Update local filter and selected items when filterState changes from parent
  useEffect(() => {
    setLocalFilter(filter2State);
    setSelectedCompanies(filter2State.companyId);
    setSelectedLicenseTypes(filter2State.licenseType);
    setSelectedStatuses(filter2State.status);
    setSelectedWorkspace(filter2State.workspace);
    setSelectedFileTypes(filter2State.fileTypes);
  }, [filter2State]);

  // Check if there are active filters
  const hasActiveFilters =
    localFilter.companyId.length > 0 ||
    localFilter.licenseType.length > 0 ||
    localFilter.status.length > 0 ||
    !!localFilter.workspace ||
    localFilter.fileTypes.length > 0;

  // Apply the selected filters
  const applyFilters = () => {
    // Use the selected states to update the filter
    const newFilter = {
      ...localFilter,
      companyId: selectedCompanies,
      licenseType: selectedLicenseTypes,
      status: selectedStatuses,
      workspace: selectedWorkspace,
      fileTypes: selectedFileTypes,
    };
    setLocalFilter(newFilter);
    onApply(newFilter);
    setIsOpen(false);
  };

  // Reset filters to default state
  const resetFilters = () => {
    const resetState = { ...defaultFilter2State };
    setLocalFilter(resetState);
    setSelectedCompanies([]);
    setSelectedLicenseTypes([]);
    setSelectedStatuses([]);
    setSelectedWorkspace('');
    setSelectedFileTypes([]);
    onApply(resetState);
  };

  // Handle company selection changes
  const handleCompanyChange = (options: SelectSitelifeOptionType[]) => {
    // Extract only the IDs from the selected options
    const selectedIds = options.map((option) => option.value as string);
    // Update the selected companies state
    setSelectedCompanies(selectedIds);
  };

  // Handle license type selection changes
  const handleLicenseTypeChange = (options: SelectSitelifeOptionType[]) => {
    // Extract the values from the selected options
    const selectedTypes = options.map((option) => option.value as string);
    // Update the selected license types state
    setSelectedLicenseTypes(selectedTypes);
  };

  // Handle status selection changes
  const handleStatusChange = (options: SelectSitelifeOptionType[]) => {
    // Extract the values from the selected options
    const selectedValues = options.map((option) => option.value as string);
    // Update the selected statuses state
    setSelectedStatuses(selectedValues);
  };

  // Handle workspace selection changes
  const handleWorkspaceChange = (option: SelectSitelifeOptionType) => {
    setSelectedWorkspace(option ? (option.value as string) : '');
  };

  // Handle file types selection changes
  const handleFileTypeChange = (options: SelectSitelifeOptionType[]) => {
    // Extract the values from the selected options
    const selectedValues = options.map((option) => option.value as string);
    // Update the selected file types state
    setSelectedFileTypes(selectedValues);
  };

  // Create workspace options
  const workspaceOptions: SelectSitelifeOptionType[] = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ];

  // Create file type options
  const fileTypeOptions: SelectSitelifeOptionType[] = [
    { label: '2D', value: '2D' },
    { label: '3D', value: '3D' },
    { label: 'Empty', value: 'empty' },
  ];

  // Get the selected workspace option
  const selectedWorkspaceOption = workspaceOptions.find(
    (option) => option.value === selectedWorkspace,
  );

  // Filter company options based on workspace selection
  const filteredCompanyOptions: SelectSitelifeOptionType[] = companies
    .filter((company) => {
      // If workspace is 'Yes', filter out companies containing 'Workspace' in their name
      if (selectedWorkspace === 'No') {
        return !company.name.includes('Workspace');
      }
      return true; // If workspace is not 'Yes', show all companies
    })
    .map((company) => ({
      label: company.name,
      value: company.id,
    }));

  // Get the selected company options based on the separate selectedCompanies state
  // and ensure they're filtered according to workspace filter
  const selectedCompanyOptions = filteredCompanyOptions.filter((option) => {
    return selectedCompanies.includes(option.value as string);
  });

  // Create license type options
  const licenseTypeOptions: SelectSitelifeOptionType[] = [
    { label: t('common:license.basic'), value: LicenseType.BASIC },
    { label: t('common:license.free'), value: LicenseType.FREE },
    { label: t('common:license.demo'), value: LicenseType.DEMO },
  ];

  // Create status options
  const statusOptions: SelectSitelifeOptionType[] = [
    {
      label: t('common:status.active', { defaultValue: 'Active' }),
      value: 'ACTIVE',
    },
    {
      label: t('common:status.archived', { defaultValue: 'Archived' }),
      value: 'ARCHIVED',
    },
  ];

  // Get the selected license type options based on the separate selectedLicenseTypes state
  const selectedLicenseTypeOptions = licenseTypeOptions.filter((option) => {
    return selectedLicenseTypes.includes(option.value as string);
  });

  // Get the selected status options based on the separate selectedStatuses state
  const selectedStatusOptions = statusOptions.filter((option) => {
    return selectedStatuses.includes(option.value as string);
  });

  // Get the selected file type options based on the separate selectedFileTypes state
  const selectedFileTypeOptions = fileTypeOptions.filter((option) => {
    return selectedFileTypes.includes(option.value as string);
  });

  return (
    <div className={cn('relative', className)}>
      <Button
        variant="secondary"
        onClick={() => setIsOpen(!isOpen)}
        className={cn({ 'bg-white': hasActiveFilters })}
      >
        <FontAwesomeIcon icon={hasActiveFilters ? fasFilter : faFilter} />
        {t('common:filterBy')}
      </Button>

      {isOpen && (
        <Paper className="absolute right-0 top-full z-10 mt-2 w-[336px] p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-shuttleGray-800">
              {t('common:filterBy')}
            </h3>
            <button
              className="cursor-pointer text-sm text-gray-800 hover:text-shuttleGray-800"
              onClick={resetFilters}
              type="button"
            >
              {t('common:button.resetFilters')}
            </button>
          </div>

          <div className="mt-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-800">
              Workspace
            </h4>
            <SelectSitelife
              className="w-full"
              options={workspaceOptions}
              value={selectedWorkspaceOption}
              onChange={handleWorkspaceChange}
              isClearable
              usePortaling
            />
          </div>

          <div className="mt-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-800">
              {t('common:organization')}
            </h4>
            <SelectSitelife
              className="w-full"
              options={filteredCompanyOptions}
              value={selectedCompanyOptions}
              onChange={handleCompanyChange}
              isMulti
              closeMenuOnSelect={false}
              usePortaling
              hideSelectedOptions={false}
            />
          </div>

          <div className="mt-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-800">
              {t('common:licenseType')}
            </h4>
            <SelectSitelife
              className="w-full"
              options={licenseTypeOptions}
              value={selectedLicenseTypeOptions}
              onChange={handleLicenseTypeChange}
              isMulti
              closeMenuOnSelect={false}
              usePortaling
              hideSelectedOptions={false}
            />
          </div>

          <div className="mt-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-800">
              {t('common:status.label')}
            </h4>
            <SelectSitelife
              className="w-full"
              options={statusOptions}
              value={selectedStatusOptions}
              onChange={handleStatusChange}
              isMulti
              closeMenuOnSelect={false}
              usePortaling
              hideSelectedOptions={false}
            />
          </div>

          <div className="mt-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-800">
              {t('common:license.fileTypes')}
            </h4>
            <SelectSitelife
              className="w-full"
              options={fileTypeOptions}
              value={selectedFileTypeOptions}
              onChange={handleFileTypeChange}
              isMulti
              closeMenuOnSelect={false}
              usePortaling
              hideSelectedOptions={false}
            />
          </div>

          <div className="mt-4 flex items-center justify-end space-x-2">
            <Button
              variant="tertiary"
              onClick={() => setIsOpen(false)}
              className="mr-2"
            >
              <FontAwesomeIcon icon={faTimes} />
              {t('common:button.abort')}
            </Button>
            <Button onClick={applyFilters}>
              {t('common:button.applyFilters')}
            </Button>
          </div>
        </Paper>
      )}
    </div>
  );
};
